// src/axiosConfig.js

import axios from "axios";
import {
  buildClientRequestId,
  captureApiLifecycle,
  captureWentOffline,
  getNetworkContext,
} from "./networkTelemetry";

axios.defaults.baseURL = import.meta.env.VITE_DATA_API;
//axios.defaults.timeout = 20000;
const GET_TIMEOUT = 20000;
const POST_TIMEOUT = 30000;

const ENDPOINT_TIMEOUTS = {
  "/payments/confirmPaymentIntent": 60000,
  "/payments/createPaymentFromStripe": 60000,
  "/lessons/delete": 60000,
  "/authenticate": 30000,
};

//let pendingCount = 0;
//export const hasPendingRequest = () => pendingCount > 0;

axios.interceptors.request.use((config) => {
  //pendingCount++;

  // Avoid duplicating the API prefix when callers already pass a prefixed path
  // (for example: baseURL=/api/ and url=/api/authenticate).
  const configuredBase = config.baseURL ?? axios.defaults.baseURL;
  if (typeof configuredBase === "string" && typeof config.url === "string") {
    const basePath = configuredBase.startsWith("http")
      ? new URL(configuredBase, window.location.origin).pathname
      : configuredBase;

    const normalizedBase =
      "/" + basePath.replace(/^\/+/, "").replace(/\/+$/, "");
    const normalizedUrl = config.url.startsWith("/")
      ? config.url
      : `/${config.url}`;

    if (
      normalizedBase !== "/" &&
      (normalizedUrl === normalizedBase ||
        normalizedUrl.startsWith(`${normalizedBase}/`))
    ) {
      config.baseURL = "";
    }
  }

  const clientRequestId = buildClientRequestId();

  config._clientRequestId = clientRequestId;
  config._requestStartedAt = Date.now();

  captureApiLifecycle({
    stage: "created",
    request_id: clientRequestId,
    api_url: config.url || null,
    api_method: config.method?.toUpperCase() ?? null,
    request_timeout: config.timeout ?? null,
  });

  if (!config.timeout) {
    const endpointTimeout = Object.entries(ENDPOINT_TIMEOUTS).find(([path]) =>
      config.url?.includes(path),
    )?.[1];

    config.timeout =
      endpointTimeout ?? (config.method === "get" ? GET_TIMEOUT : POST_TIMEOUT);
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    // pendingCount--;
    const startedAt = response.config?._requestStartedAt;
    const durationMs =
      typeof startedAt === "number" ? Date.now() - startedAt : null;

    captureApiLifecycle({
      stage: "completed",
      request_id: response.config?._clientRequestId || null,
      api_url: response.config?.url || null,
      api_method: response.config?.method?.toUpperCase() ?? null,
      http_status: response.status,
      duration_ms: durationMs,
    });

    return response;
  },
  (error) => {
    //  pendingCount--;
    console.log("axios.interceptors.error");
    console.log(error.code);
    console.log("name:", error.name); // ← add this
    console.log("message:", error.message); // ← add this
    console.log("isCancel:", axios.isCancel(error)); // ← add this
    console.log("500 exception:", error.exeption); // ← add this

    if (
      axios.isCancel(error) ||
      error.message === "canceled" ||
      error.code === "ERR_CANCELED"
    ) {
      return Promise.reject(error); // pass through, don't capture
    }

    const status = error.response?.status;
    const url = error.config?.url ?? "";
    const isOffline = !navigator.onLine;
    const isNetworkError = !error.response;
    const isTimeout = error.code === "ECONNABORTED";
    const startedAt = error.config?._requestStartedAt;
    const durationMs =
      typeof startedAt === "number" ? Date.now() - startedAt : null;
    const request = error.request;
    const clientRequestId = error.config?._clientRequestId || null;

    const inferredStage = !request
      ? "pre_request"
      : request.readyState === 0
        ? "created_not_sent"
        : request.readyState === 4
          ? "sent_no_success_response"
          : "in_flight";

    if (isOffline && isNetworkError) {
      captureWentOffline({
        source: "axios",
        api_url: url || null,
        api_method: error.config?.method?.toUpperCase() ?? null,
        request_id: clientRequestId,
      });
      return Promise.reject(error);
    }

    const rawApiUrl = error.request?.responseURL || error.config?.url || null;
    let apiUrlPath = rawApiUrl;

    if (rawApiUrl) {
      try {
        const parsed = new URL(rawApiUrl, window.location.origin);
        apiUrlPath = `${parsed.pathname}${parsed.search}`;
      } catch {
        apiUrlPath = rawApiUrl;
      }
    }

    captureApiLifecycle({
      stage: "failed",
      failure_stage: inferredStage,
      request_id: clientRequestId,
      api_url: apiUrlPath,
      api_method: error.config?.method?.toUpperCase() ?? null,
      duration_ms: durationMs,
      axios_error_code: error.code ?? null,
      request_ready_state: request?.readyState ?? null,
      request_status: request?.status ?? null,
      request_response_url: request?.responseURL ?? null,
      ...getNetworkContext(),
    });

    window.posthog?.captureException(error, {
      api_url: apiUrlPath,
      api_method: error.config?.method?.toUpperCase(),
      http_status: error.response?.status ?? null,
      http_status_text: error.response?.statusText ?? null,
      request_timeout: error.config?.timeout,
      request_id: clientRequestId,
      failure_stage: inferredStage,
      duration_ms: durationMs,
      request_ready_state: request?.readyState ?? null,
      request_status: request?.status ?? null,
      request_response_url: request?.responseURL ?? null,
      is_timeout: error.code === "ECONNABORTED",
      is_network_error: isNetworkError && !isTimeout,
      is_offline_error: isOffline && isNetworkError,
      axios_error_code: error.code,
      is_cors_likely: isNetworkError && !isTimeout && navigator.onLine,
      page_path: window.location.pathname,
      retry_attempt: error.config?._retryAttempt ?? 0, // ← add
      is_retry: (error.config?._retryAttempt ?? 0) > 0, // ← add
      skipped_global_handler: !!error.config?.skipGlobalErrorHandler, // ← useful to track
      ...getNetworkContext(),
    });

    return Promise.reject(error);
  },
);
